// シェイプレイヤーのラスタライズ (OffscreenCanvas 2D)。
import type { ShapeLayer } from '@/types/document'
import type { Rect } from '@/types/geometry'
import type { RgbaImage } from '@/pixels/transform'
import { geometryCommands, shapeBounds } from './shapes'
import { hexToRgb } from '@/color/convert'

export function rasterizeShape(layer: ShapeLayer): { rect: Rect; image: RgbaImage } | null {
  const strokePad = layer.strokeHex ? (layer.strokeAlign === 'inside' ? 0 : layer.strokeAlign === 'center' ? layer.strokeWidth / 2 : layer.strokeWidth) : 0
  const rect = shapeBounds(layer.geometry, layer.transform, strokePad)
  if (rect.w > 16384 || rect.h > 16384) return null
  const canvas = new OffscreenCanvas(rect.w, rect.h)
  const c = canvas.getContext('2d')!
  const m = layer.transform
  c.setTransform(m[0], m[1], m[3], m[4], m[6] - rect.x, m[7] - rect.y)
  const path = new Path2D()
  for (const cmd of geometryCommands(layer.geometry)) {
    if (cmd[0] === 'M') path.moveTo(cmd[1], cmd[2])
    else if (cmd[0] === 'L') path.lineTo(cmd[1], cmd[2])
    else if (cmd[0] === 'C') path.bezierCurveTo(cmd[1], cmd[2], cmd[3], cmd[4], cmd[5], cmd[6])
    else path.closePath()
  }
  const rgba = (hex: string, a: number) => {
    const col = hexToRgb(hex) ?? { r: 0, g: 0, b: 0 }
    return `rgba(${col.r},${col.g},${col.b},${a})`
  }
  const isLine = layer.geometry.type === 'line' || (layer.geometry.type === 'path' && !layer.geometry.closed)
  if (layer.fillHex && !isLine) {
    c.fillStyle = rgba(layer.fillHex, layer.fillAlpha)
    c.fill(path, 'nonzero')
  }
  if (layer.strokeHex && layer.strokeWidth > 0) {
    c.strokeStyle = rgba(layer.strokeHex, layer.strokeAlpha)
    c.lineJoin = 'round'
    c.lineCap = 'round'
    if (layer.strokeAlign === 'center' || isLine) {
      c.lineWidth = layer.strokeWidth
      c.stroke(path)
    } else {
      // 内側 / 外側: 2 倍幅で描いてクリップ
      c.save()
      c.lineWidth = layer.strokeWidth * 2
      if (layer.strokeAlign === 'inside') {
        c.clip(path, 'nonzero')
        c.stroke(path)
      } else {
        // 外側: パスの外だけ残す (evenodd で全体からパスを引く)
        const outer = new Path2D()
        outer.rect(-1e6, -1e6, 2e6, 2e6)
        outer.addPath(path)
        c.clip(outer, 'evenodd')
        c.stroke(path)
      }
      c.restore()
    }
  }
  const img = c.getImageData(0, 0, rect.w, rect.h)
  return { rect, image: { width: rect.w, height: rect.h, data: img.data } }
}
